import { useState, useEffect } from "react";
import { api } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { ShoppingBag, Package, Music2, Check, Clock, X, Truck, AlertTriangle } from "lucide-react";

const FULFILLMENT_OPTIONS = ["unfulfilled", "fulfilled", "shipped", "delivered"];

const FILTERS = [
  { key: "all", label: "All" },
  { key: "paid", label: "Paid" },
  { key: "pending", label: "Pending" },
  { key: "abandoned", label: "Abandoned" },
  { key: "expired", label: "Failed" },
];

function StatusBadge({ status }) {
  const map = {
    paid: { cls: "bg-green-900/30 border-green-700/40 text-green-400", icon: Check },
    pending: { cls: "bg-yellow-900/30 border-yellow-700/40 text-yellow-400", icon: Clock },
    abandoned: { cls: "bg-white/5 border-white/10 text-white/30", icon: AlertTriangle },
    expired: { cls: "bg-red-900/30 border-red-700/40 text-red-400", icon: X },
  };
  const s = map[status] || map.pending;
  const Icon = s.icon;
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold px-2 py-1 border rounded ${s.cls}`}>
      <Icon className="w-3 h-3" /> {status}
    </span>
  );
}

function FulfillmentBadge({ status }) {
  const map = {
    unfulfilled: "bg-white/5 border-white/10 text-white/40",
    fulfilled: "bg-[#D4AF37]/10 border-[#D4AF37]/30 text-[#D4AF37]",
    shipped: "bg-blue-900/30 border-blue-700/40 text-blue-400",
    delivered: "bg-green-900/30 border-green-700/40 text-green-400",
  };
  return (
    <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-1 border rounded ${map[status] || map.unfulfilled}`}>
      {status}
    </span>
  );
}

export default function OrdersManager() {
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState({});
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);

  const fetchOrders = (f) => {
    const q = f && f !== "all" ? `?status=${f}` : "";
    api.get(`/orders${q}`).then(r => { setOrders(r.data); setLoading(false); }).catch(() => setLoading(false));
  };
  const fetchStats = () => api.get("/orders/stats").then(r => setStats(r.data)).catch(() => {});

  useEffect(() => { fetchOrders(filter); fetchStats(); }, [filter]);

  const updateFulfillment = async (orderId, status) => {
    try {
      await api.put(`/orders/${orderId}/fulfillment`, { fulfillment_status: status });
      toast.success(`Order marked as ${status}`);
      fetchOrders(filter);
    } catch { toast.error("Update failed"); }
  };

  const getCount = (key) => {
    if (key === "all") return stats.total || 0;
    if (key === "expired") return stats.expired || 0;
    return stats[key] || 0;
  };

  return (
    <div data-testid="admin-orders-manager">
      <div className="mb-6">
        <h1 className="font-heading text-3xl">ORDERS</h1>
        <p className="text-white/40 text-sm mt-1">{stats.paid || 0} paid · ${orders.filter(o => o.payment_status === "paid").reduce((s, o) => s + (o.amount || 0), 0).toFixed(2)} revenue</p>
      </div>

      {/* Filter tabs */}
      <div data-testid="orders-filter-bar" className="flex items-center gap-2 mb-6 overflow-x-auto pb-2 scrollbar-hide">
        {FILTERS.map(f => (
          <button
            key={f.key}
            data-testid={`orders-filter-${f.key}`}
            onClick={() => { setFilter(f.key); setLoading(true); }}
            className={`flex items-center gap-1.5 text-xs uppercase tracking-wider font-semibold px-4 py-2 border whitespace-nowrap transition-all duration-300 ${
              filter === f.key
                ? "border-[#D4AF37] text-[#D4AF37] bg-[#D4AF37]/10"
                : "border-white/10 text-white/40 hover:border-white/20 hover:text-white/60"
            }`}
          >
            {f.label}
            <span className={`text-[9px] ml-0.5 rounded-full min-w-[18px] h-[18px] flex items-center justify-center ${
              filter === f.key ? "bg-[#D4AF37]/20 text-[#D4AF37]" : "bg-white/5 text-white/30"
            }`}>{getCount(f.key)}</span>
          </button>
        ))}
      </div>

      {loading ? <p className="text-white/30">Loading...</p> : orders.length === 0 ? (
        <div className="text-center py-16 text-white/30">
          <ShoppingBag className="w-12 h-12 mx-auto mb-4 opacity-30" />
          <p>{filter === "all" ? "No orders yet." : `No ${filter} orders.`}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {orders.map(order => {
            const isMusic = ["music", "single", "ep", "album", "exclusive", "digital"].includes(order.product_type);
            const Icon = isMusic ? Music2 : Package;
            const isAbandoned = order.payment_status === "abandoned" || order.payment_status === "expired";
            return (
              <div key={order.id} data-testid={`order-${order.id}`} className={`bg-[#111111] border p-4 rounded transition-colors ${isAbandoned ? "border-white/[0.03] opacity-60" : "border-white/5 hover:border-white/10"}`}>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-[#1A1A1A] rounded flex items-center justify-center flex-shrink-0">
                    <Icon className="w-5 h-5 text-white/20" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <p className="text-sm font-semibold truncate">{order.product_title}</p>
                      <StatusBadge status={order.payment_status} />
                      {order.payment_status === "paid" && <FulfillmentBadge status={order.fulfillment_status} />}
                    </div>
                    <div className="flex items-center gap-3 text-xs text-white/40 flex-wrap">
                      <span>{order.customer_email || "No email"}</span>
                      <span className="capitalize">{order.product_type}</span>
                      <span className="text-[#D4AF37] font-semibold">${(order.amount || 0).toFixed(2)}</span>
                      <span>{new Date(order.created_at).toLocaleDateString()}</span>
                    </div>
                    {order.shipping && order.shipping.name && (
                      <div className="flex items-center gap-1 mt-2 text-[10px] text-white/30">
                        <Truck className="w-3 h-3" />
                        {order.shipping.name} · {order.shipping.address}, {order.shipping.city} {order.shipping.state} {order.shipping.zip}
                      </div>
                    )}
                  </div>
                  <div className="flex-shrink-0">
                    {order.payment_status === "paid" && (
                      <select
                        data-testid={`fulfillment-select-${order.id}`}
                        value={order.fulfillment_status}
                        onChange={e => updateFulfillment(order.id, e.target.value)}
                        className="bg-[#0A0A0A] border border-white/10 text-xs text-white p-1.5 rounded focus:border-[#D4AF37] outline-none"
                      >
                        {FULFILLMENT_OPTIONS.map(s => (
                          <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
                        ))}
                      </select>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
